<template>
    <pendo-popover-v2
        v-if="!isAutoProvisionedTrialExpired"
        ref="notificationsPopover"
        :title="$t('notifications.Notifications')"
        placement="bottom-end"
        :hover="false"
        :popper-class="['notifications__popover', 'top-nav__popover']"
        keyboard-navigation
        @show="focusFirstOption"
        @hide="$emit('select')">
        <template #default="{ shown, ...triggerAttrs }">
            <button
                v-pendo-tooltip="{
                    content: $t('notifications.Notifications'),
                    arrow: true,
                }"
                v-bind="triggerAttrs"
                data-cy="notifications-dropdown-trigger"
                pendo-analytics="notifications-nav-item"
                class="top-nav__trigger top-nav__button-trigger"
                :class="{
                    'is-open': shown,
                }"
                :aria-label="$t('notifications.Notifications')"
                @keydown.tab="handleTabKeydown($event, shown, triggerAttrs)">
                <notifications-icon :any-unread="anyUnread" />
            </button>
        </template>
        <template #popover>
            <div
                ref="notificationsContent"
                class="notifications__popover-content"
                data-cy="notifications-dropdown-content">
                <pendo-loading-indicator v-if="isLoading" />
                <template v-else>
                    <div class="notifications__header">
                        <h2 class="nav-title">
                            {{ $t('notifications.Notifications') }}
                        </h2>
                        <pendo-button
                            v-if="anyUnread"
                            :loading="isMarkingRead"
                            data-cy="mark-all-as-read-button"
                            icon="check"
                            size="small"
                            type="tertiary"
                            class="notifications__mark-as-read"
                            :label="$t('notifications.Mark all as read')"
                            @click="markNotificationsAsRead" />
                    </div>
                    <div class="notifications__list">
                        <template v-if="formattedNotifications.length === 0">
                            <div
                                class="notifications__empty-state"
                                data-cy="notifications-empty-state">
                                <pendo-empty-state
                                    :title="$t('notifications.No notifications yet')"
                                    :description="
                                        $t(
                                            'notifications.We\'ll let you know when someone engages with your comments or mentions you.',
                                        )
                                    ">
                                    <template #icon>
                                        <img
                                            class="notifications__empty-state-image"
                                            src="@img/notification-empty-state.svg">
                                    </template>
                                </pendo-empty-state>
                            </div>
                        </template>
                        <template v-else>
                            <notification-list
                                :notifications="last7DaysNotifications"
                                :label="$t('notifications.Last 7 days')"
                                @card-clicked="onCardClicked"
                                @manage-access="onManageAccess" />
                            <notification-list
                                :notifications="olderThan7DaysNotifications"
                                :label="$t('notifications.Older than 7 days')"
                                @card-clicked="onCardClicked"
                                @manage-access="onManageAccess" />
                        </template>
                    </div>
                </template>
            </div>
        </template>
    </pendo-popover-v2>
</template>

<script>
import {
    PendoTooltip,
    PendoPopoverV2,
    PendoButton,
    PendoEmptyState,
    PendoLoadingIndicator,
} from '@pendo/components-vue3';
import NotificationsIcon from '@/top-nav/components/icons/notifications-icon.vue';
import { formatUserFullName } from '@utils/user-settings';
import {
    formatTimestamp,
    groupByDateRange,
    isBulkNotification,
    markInAppNotificationAsRead,
    markAllInAppNotificationsAsRead,
} from '@/collaboration/notifications/notifications.utils';
import NotificationList from '@/collaboration/notifications/notification-list.vue';
import { DRAWERS } from '@/voc/constants/overlays.js';
import { useDrawers } from '@/voc/use/use-drawers.js';
import { mapGetters, mapActions } from 'vuex';
import { mapState as mapPiniaState } from 'pinia';
import { usePendoCollaborationStore } from '@/stores/pendo-collaboration.store';
import { getFirstFocusableNode } from '@/utils/dom.utils.js';
import { getNotifications, subscribeToDocument, SOCKET_CONNECTION_STATUSES } from '@/comments/comments.utils';

export default {
    name: 'NotificationsDropdown',
    components: {
        PendoPopoverV2,
        PendoButton,
        NotificationsIcon,
        NotificationList,
        PendoEmptyState,
        PendoLoadingIndicator,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        preventTabKeyNavigation: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['select'],
    data () {
        return {
            notifications: [],
            notificationSubscription: null,
            isLoading: true,
            isMarkingRead: false,
            unsubscribeFromSocket: null,
        };
    },
    computed: {
        ...mapGetters({
            dashboardById: 'dashboards/dashboardById',
            isAutoProvisionedTrialExpired: 'subscription/isAutoProvisionedTrialExpired',
            users: 'users/list',
        }),
        ...mapPiniaState(usePendoCollaborationStore, {
            connectionStatus: 'getConnectionStatus',
        }),
        formattedNotifications () {
            return this.notifications.map((notification) => {
                const formattedNotification = {
                    ...notification,
                    formattedTimestamp: notification.formattedTimestamp || formatTimestamp(notification.timestamp),
                    isUnread: notification.isUnread === undefined ? true : notification.isUnread,
                };

                if (notification.type === 'InAppCommentThread') {
                    const user = this.getUserObjectByUserId(notification.notification.userId);

                    formattedNotification.user = user;
                    formattedNotification.isUnread = !notification.read;
                }

                if(isBulkNotification(notification)) {
                    formattedNotification.isUnread = !notification.read;
                }

                return formattedNotification;
            });
        },
        last7DaysNotifications () {
            return groupByDateRange(this.formattedNotifications).last7Days.slice().sort((a, b) => b.timestamp - a.timestamp);
        },
        olderThan7DaysNotifications () {
            return groupByDateRange(this.formattedNotifications).olderThan7Days.slice().sort((a, b) => b.timestamp - a.timestamp);
        },
        anyUnread () {
            return this.formattedNotifications.some((notification) => notification.isUnread);
        },
    },
    watch: {
        connectionStatus: {
            async handler (status) {
                if (status !== SOCKET_CONNECTION_STATUSES.CONNECTED) return;

                this.isLoading = true;
                try {
                    this.unsubscribeFromSocket = await subscribeToDocument({
                        documentId: 'notifications',
                        callback: this.handleNewNotifications,
                    });
                } catch (err) {
                    console.error(err);
                    this.isLoading = false;

                    return;
                }

                try {
                    await Promise.all([
                        getNotifications(),
                        this.loadUsers(),
                    ]);
                } catch (err) {
                    console.error(err);
                } finally {
                    this.isLoading = false;
                }
            },
            immediate: true,
        },
        'formattedNotifications.length': {
            handler (newVal) {
                if (newVal > 0 && this.$refs.popoverRef?.isShown) {
                    this.$nextTick(() => {
                        this.focusFirstOption();
                    });
                }
            },
            immediate: true,
        },
    },
    beforeUnmount () {
        this.notificationSubscription?.unsubscribe();
        if (this.unsubscribeFromSocket) this.unsubscribeFromSocket();
    },
    methods: {
        ...mapActions({
            loadOneDashboard: 'dashboards/loadOne',
            loadUsers: 'users/loadAll',
        }),
        getUserObjectByUserId (userId) {
            const user = this.users.find((user) => String(user.id) === String(userId));

            if (user) {
                return {
                    fullName: formatUserFullName(user),
                    username: user.username,
                };
            }

            return {
                fullName: 'Unknown user',
                username: 'Unknown email',
            };
        },
        focusFirstOption () {
            setTimeout(() => {
                const firstFocusableNode = getFirstFocusableNode(this.$refs.notificationsContent);
                if (firstFocusableNode) {
                    firstFocusableNode.focus();
                }
            }, 50);
        },
        hide () {
            this.$refs.notificationsPopover?.hide?.();
        },
        handleNewNotifications (message) {
            if (message.type !== 'notifications') return;

            this.notifications = message.data;
        },
        async onCardClicked (notificationId) {
            const notification = this.notifications.find((notif) => notif.id === notificationId);
            if (!notification) {
                return;
            }

            this.hide();
            this.$emit('select');

            if (notification.type === 'InAppCommentThread') {
                await this.handleInAppCommentNotification(notification);
            } else if (isBulkNotification(notification)) {
                await this.handleBulkNotification(notification);
            } else if (notification.notificationSource === 'comment') {
                await this.handleCommentNotification(notification);
            } else {
                await this.handleRequestNotification(notification);
            }
        },
        async handleInAppCommentNotification (notification) {
            await markInAppNotificationAsRead(notification.id);
            notification.read = true;
        },
        async handleBulkNotification (notification) {
            await markInAppNotificationAsRead(notification.id);
            notification.read = true;
        },
        async handleCommentNotification (notification) {
            const { targetElementId, pageInfo, context, metadata } = notification.notificationSourceData;
            const path = notification.notificationSourceData.pageInfo.path.replace(/\/s\/\d+/, ''); // strip /s/:subId from path

            const query = { commentId: notification.notificationSourceData.annotationId };

            const pathSegments = path.split('/');
            if (pathSegments[1] === 'dashboards') {
                query.widgetId = targetElementId;
            } else if (pathSegments[1] === 'listen') {
                const { openItem } = Object.fromEntries([...new URLSearchParams(pageInfo.commentUrl.split('?')[1])]);
                query.openItem = openItem ?? metadata?.clientDocumentId?.split('/')[2] ?? '';
            }

            if (context?.customUrl) {
                const { pathname, searchParams } = new URL(context.customUrl);
                const queryParams = Object.fromEntries(searchParams);
                const href = this.$router.pendo.toPath({ path: pathname, query: queryParams });
                this.$router.push(href);
            } else {
                const href = this.$router.pendo.toPath({ path, query });
                this.$router.push(href);
            }

            if (pathSegments[1] === 'listen') {
                if (query.openItem) {
                    const { openDrawer } = useDrawers('voc');

                    openDrawer({
                        drawer: DRAWERS.vocItem,
                        metadata: {
                            vocItemId: query.openItem,
                            type: pathSegments[2] === 'validate' ? 'idea' : 'feedback',
                        },
                    });
                }
            }
        },
        async handleRequestNotification (notification) {
            if (!notification || !notification.notification?.messageMetadata) {
                return;
            }
            const { requestEntityId } = notification.notification.messageMetadata;
            const hasDashboard = this.dashboardById(requestEntityId);

            let canAccess = hasDashboard;

            if (!canAccess) {
                canAccess = await this.checkDashboardAccess(requestEntityId);
            }

            if (canAccess) {
                const dashboardPath = this.$router.pendo.toPath(`/dashboards/${requestEntityId}`);
                this.$router.push(dashboardPath);
            }
        },
        async checkDashboardAccess (dashboardId) {
            try {
                await this.loadOneDashboard({ id: dashboardId, reFetch: true });

                return !!this.dashboardById(dashboardId);
            } catch {
                return false;
            }
        },
        async onManageAccess (data) {
            const { entityType, entityId } = data;

            if (entityType === 'Dashboard') {
                const href = this.$router.pendo.toPath({
                    name: 'Dashboard',
                    params: { id: entityId },
                    query: { showShareModal: true },
                });
                this.$router.push(href);
            }

            this.hide();
            this.$emit('select');
        },
        async markNotificationsAsRead () {
            try {
                this.isMarkingRead = true;
                await markAllInAppNotificationsAsRead();
                this.notifications.forEach((notification) => {
                    notification.isUnread = false;
                    notification.read = true;
                });
            } catch (err) {
                console.error(err);
            } finally {
                this.isMarkingRead = false;
            }
        },
        handleTabKeydown ($event, shown, triggerAttrs) {
            if (this.preventTabKeyNavigation) {
                $event.preventDefault();

                if (shown) {
                    triggerAttrs.hide();
                } else {
                    triggerAttrs.show();
                }
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

:deep(.top-nav__button-trigger) {
    .filled-icon,
    .empty-icon {
        transition: none;
    }
}

:deep(.pendo-loading-parent--relative) {
    height: 300px;
    width: 350px;

    .pendo-loading {
        height: 100%;
    }
}

.nav-title {
    padding: 0;
    margin: 0;
}

.notifications {
    &__popover-content {
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    &__header {
        padding: 12px 24px 12px 24px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid var(--surface-secondary-hover);
    }

    &__mark-as-read {
        color: var(--text-body-default-base);
        font-size: 13px;
        height: 20px;
        line-height: 20px;
    }

    &__list {
        flex-grow: 1;
        padding: 0 12px 12px 12px;
    }

    &__empty-state {
        padding: 40px;
        display: flex;
        justify-content: center;

        :deep(.pendo-empty-state) {
            position: unset;
            width: 300px;
            height: unset;
        }

        :deep(.pendo-empty-state__inner) {
            max-width: unset;
        }

        :deep(.pendo-empty-state__title) {
            font-size: 14px;
            line-height: 150%;
            color: var(--text-body-default-base);
        }

        :deep(.pendo-empty-state__description) {
            font-size: 13px;
            line-height: 150%;
            color: var(--text-body-subtle-base);
        }
    }
}
</style>

<style lang="less">
.top-nav__popover.notifications__popover {
    .pendo-popover-v3__panel {
        width: 350px;
        padding: 0;
    }

    .notifications__popover-content:focus-visible {
        outline: 2px solid var(--palette-gray-90);
        outline-offset: -1px;
        border-radius: 4px;
    }
}
</style>
